/**
 * Cross-platform share utility.
 * Uses @capacitor/share on native iOS/Android, navigator.share on web,
 * and falls back to clipboard.
 */

import { Capacitor } from '@capacitor/core';

interface ShareOpts {
  title?: string;
  text: string;
  url?: string;
}

export async function shareText(opts: ShareOpts): Promise<'shared' | 'copied' | 'cancelled'> {
  // Native share via Capacitor plugin
  if (Capacitor.isNativePlatform()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ ...opts, dialogTitle: opts.title });
      return 'shared';
    } catch {
      return 'cancelled';
    }
  }

  // Web Share API
  if (navigator.share) {
    try {
      await navigator.share(opts);
      return 'shared';
    } catch {
      // User cancelled or API failed — try clipboard
    }
  }

  // Clipboard fallback
  try {
    const content = opts.url ? `${opts.text}\n${opts.url}` : opts.text;
    await navigator.clipboard.writeText(content);
    return 'copied';
  } catch {
    return 'cancelled';
  }
}
